/**
 * X-Ray Contributions Component
 *
 * Displays contribution analysis for a selected field or exposure group.
 * Shows how each holding contributes to the portfolio total.
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronUp,
  faChevronDown,
  faChevronRight,
  faSearch,
  faInfoCircle,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { faExclamationTriangle } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/ui/table/Table";
import { Badge } from "@/components/ui/badge/Badge";
import { Button } from "@/components/ui/button/Button";
import clsx from "clsx";
import {
  getXRayContribution,
  getXRayContributionOptions,
  type XRayContributionResponse,
  type XRayContributionOptionsResponse,
  type XRayContributionField,
  type XRayExposureGroup,
  type HoldingsSource,
} from "@/services/api/portfolios";
import {
  fetchScreenerColumns,
  type ScreenerColumn,
} from "@/services/api/screener";

interface XRayContributionsProps {
  portfolioId: string;
}

type AnalysisType = "field" | "exposure";

// Searchable dropdown component
interface SearchableSelectProps<T> {
  items: T[];
  value: string;
  onChange: (value: string) => void;
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  placeholder?: string;
}

function SearchableSelect<T>({
  items,
  value,
  onChange,
  getId,
  getLabel,
  placeholder = "Search...",
}: SearchableSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedItem = items.find((item) => getId(item) === value);
  const selectedLabel = selectedItem ? getLabel(selectedItem) : "";

  const filteredItems = useMemo(() => {
    if (!search) return items;
    const lowerSearch = search.toLowerCase();
    return items.filter((item) =>
      getLabel(item).toLowerCase().includes(lowerSearch)
    );
  }, [items, search, getLabel]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative min-w-[200px]">
      {/* Trigger button - Meridian pill style */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          "w-full px-4 py-2 text-sm text-left rounded-xl",
          "bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600",
          "text-slate-900 dark:text-slate-100",
          "shadow-sm hover:shadow-md transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-indigo-500/50",
          "flex items-center justify-between gap-2"
        )}
      >
        <span className="truncate font-medium">{selectedLabel || "Select..."}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={clsx(
            "h-3 w-3 text-slate-400 transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Dropdown - Meridian card style */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl overflow-hidden">
          {/* Search input */}
          <div className="p-3 border-b border-slate-100 dark:border-slate-700">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400"
              />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={placeholder}
                className={clsx(
                  "w-full pl-9 pr-3 py-2 text-sm rounded-lg",
                  "bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600",
                  "text-slate-900 dark:text-slate-100 placeholder-slate-400",
                  "focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
                )}
              />
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-56 overflow-y-auto">
            {filteredItems.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 text-center">
                No matches found
              </div>
            ) : (
              filteredItems.map((item) => {
                const id = getId(item);
                const label = getLabel(item);
                const isSelected = id === value;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      onChange(id);
                      setIsOpen(false);
                      setSearch("");
                    }}
                    className={clsx(
                      "w-full px-4 py-2.5 text-sm text-left transition-colors duration-150",
                      "hover:bg-slate-50 dark:hover:bg-slate-700/50",
                      isSelected
                        ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 font-medium"
                        : "text-slate-700 dark:text-slate-200"
                    )}
                  >
                    {label}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function XRayContributions({ portfolioId }: XRayContributionsProps) {
  const navigate = useNavigate();
  const osContext = useOsActionsOptional();

  // Options loaded from API
  const [options, setOptions] = useState<XRayContributionOptionsResponse | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // User selection
  const [analysisType, setAnalysisType] = useState<AnalysisType>("field");
  const [selectedField, setSelectedField] = useState<string>("");
  const [selectedExposure, setSelectedExposure] = useState<string>("");
  const [holdingsSource, setHoldingsSource] = useState<HoldingsSource>("latest");

  // Data
  const [data, setData] = useState<XRayContributionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sorting
  const [sortBy, setSortBy] = useState("weight");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Top N categories for exposure mode
  type TopNOption = 5 | 10 | 20 | "all";
  const [topN, setTopN] = useState<TopNOption>(5);

  // Group by exposure group's primary classification
  const [groupBy, setGroupBy] = useState<string>("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Numeric banding for field mode - groups values into ranges
  const [bandingEnabled, setBandingEnabled] = useState(false);
  const [bandSize, setBandSize] = useState(1.0);
  const [bandStart, setBandStart] = useState(0.5);


  // Screener columns for formatting info (shared with Table View)
  const [screenerColumns, setScreenerColumns] = useState<ScreenerColumn[]>([]);

  // Load screener columns for formatting info
  useEffect(() => {
    async function loadColumns() {
      try {
        const columns = await fetchScreenerColumns();
        setScreenerColumns(columns);
      } catch (err) {
        console.error("Failed to load screener columns:", err);
      }
    }
    loadColumns();
  }, []);

  // Load contribution options
  useEffect(() => {
    async function fetchOptions() {
      setOptionsLoading(true);
      setOptionsError(null);
      try {
        const response = await getXRayContributionOptions(portfolioId);
        setOptions(response);

        // Set defaults - prefer ongoing_charge to match table view
        if (response.fundamental_fields.length > 0) {
          // Try to find ongoing charge field by common IDs or name
          const ongoingChargeField = response.fundamental_fields.find(
            (f) =>
              f.id === "ongoing_charge" ||
              f.id === "overall_ongoing_fees" ||
              f.id.includes("ongoing") ||
              f.name.toLowerCase().includes("ongoing")
          );
          setSelectedField(ongoingChargeField?.id || response.fundamental_fields[0].id);
        }
        if (response.exposure_groups.length > 0) {
          setSelectedExposure(response.exposure_groups[0].id);
        }
      } catch (err) {
        console.error("Failed to fetch contribution options:", err);
        setOptionsError(err instanceof Error ? err.message : "Failed to load options");
      } finally {
        setOptionsLoading(false);
      }
    }

    fetchOptions();
  }, [portfolioId]);

  // Load contribution data when selection changes
  useEffect(() => {
    async function fetchData() {
      const selection = analysisType === "field" ? selectedField : selectedExposure;
      if (!selection) return;

      setLoading(true);
      setError(null);

      try {
        const request =
          analysisType === "field"
            ? { field: selectedField, group_by: groupBy || undefined, holdings_source: holdingsSource }
            : { exposure: selectedExposure, group_by: groupBy || undefined, holdings_source: holdingsSource };

        const response = await getXRayContribution(portfolioId, request);
        setData(response);

        // Reset sort to appropriate default
        if (analysisType === "field") {
          setSortBy("contribution");
        } else {
          setSortBy("weight");
        }
        setSortDirection("desc");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch contribution data");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [portfolioId, analysisType, selectedField, selectedExposure, groupBy, holdingsSource]);

  // Handle sorting
  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnId);
      setSortDirection("desc");
    }
  };

  // Unique identifier for the "remaining categories" bucket - can't conflict with real category names
  const OTHER_BUCKET = "__OTHER__";

  // Categories to display - show top N by total, bundle rest into bucket
  const { displayCategories, otherCategories } = useMemo(() => {
    if (data?.mode !== "exposure" || !data.categories) {
      return { displayCategories: [], otherCategories: [] };
    }

    const categories = data.categories;
    const totals = data.totals || {};
    const limit = topN === "all" ? categories.length : topN;

    // No need for bucket if we can show all categories
    if (categories.length <= limit) {
      return { displayCategories: categories, otherCategories: [] };
    }

    // Sort by absolute total descending, take top N
    const sorted = [...categories].sort((a, b) =>
      Math.abs(totals[b] || 0) - Math.abs(totals[a] || 0)
    );

    return {
      displayCategories: [...sorted.slice(0, limit), OTHER_BUCKET],
      otherCategories: sorted.slice(limit),
    };
  }, [data?.mode, data?.categories, data?.totals, topN]);

  // Sort rows (must be after otherCategories is defined)
  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];

    // Helper to get value for sorting, handling bucket and total columns
    const getSortValue = (row: any, key: string): any => {
      // Check if sorting by total columns
      if (key === "value_total") {
        if (!data?.categories) return 0;
        return data.categories.reduce((sum, cat) => sum + (row[`value_${cat}`] || 0), 0);
      }
      if (key === "contrib_total") {
        if (!data?.categories) return 0;
        return data.categories.reduce((sum, cat) => sum + (row[`contrib_${cat}`] || 0), 0);
      }
      // Check if sorting by a bucket column
      if (key.endsWith(`_${OTHER_BUCKET}`) && otherCategories.length > 0) {
        const prefix = key.replace(`_${OTHER_BUCKET}`, "");
        if (prefix === "pct") {
          const contribSum = otherCategories.reduce((sum, cat) => sum + (row[`contrib_${cat}`] || 0), 0);
          const totalSum = otherCategories.reduce((sum, cat) => sum + (data?.totals?.[cat] || 0), 0);
          return totalSum === 0 ? 0 : (contribSum / totalSum) * 100;
        }
        return otherCategories.reduce((sum, cat) => sum + (row[`${prefix}_${cat}`] || 0), 0);
      }
      return row[key];
    };

    return [...data.rows].sort((a, b) => {
      const aVal = getSortValue(a, sortBy);
      const bVal = getSortValue(b, sortBy);

      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Handle string comparison (name column)
      if (typeof aVal === "string" && typeof bVal === "string") {
        const result = aVal.localeCompare(bVal);
        return sortDirection === "asc" ? result : -result;
      }

      // Handle numeric comparison
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });
  }, [data?.rows, data?.totals, sortBy, sortDirection, otherCategories]);

  // Calculate "Other" aggregated values for each row
  const getOtherValue = (row: any, prefix: string): number => {
    if (otherCategories.length === 0) return 0;

    // For pct (% of Total), calculate properly: sum(contributions) / sum(totals) * 100
    if (prefix === "pct") {
      const contribSum = otherCategories.reduce(
        (sum, cat) => sum + (row[`contrib_${cat}`] || 0),
        0
      );
      const totalSum = otherCategories.reduce(
        (sum, cat) => sum + (data?.totals?.[cat] || 0),
        0
      );
      if (totalSum === 0) return 0;
      return (contribSum / totalSum) * 100;
    }

    // For value and contrib, just sum
    let sum = 0;
    for (const cat of otherCategories) {
      sum += row[`${prefix}_${cat}`] || 0;
    }
    return sum;
  };

  // Calculate total value across all categories for a row (including "Other" bucket)
  const getRowValueTotal = (row: any): number => {
    if (!data?.categories) return 0;
    let sum = 0;
    for (const cat of data.categories) {
      sum += row[`value_${cat}`] || 0;
    }
    return sum;
  };

  // Calculate total contribution across all categories for a row (including "Other" bucket)
  const getRowContribTotal = (row: any): number => {
    if (!data?.categories) return 0;
    let sum = 0;
    for (const cat of data.categories) {
      sum += row[`contrib_${cat}`] || 0;
    }
    return sum;
  };

  // Calculate raw contribution total and scale factor for normalization
  const exposureScaling = useMemo(() => {
    if (!data?.totals || data.mode !== "exposure") {
      return { rawTotal: 100, scaleFactor: 1, needsScaling: false };
    }

    // Sum of all category totals = raw contribution total
    const rawTotal = Object.values(data.totals).reduce((sum: number, val: any) => sum + (val || 0), 0);

    // Scale factor to normalize to 100%
    const scaleFactor = rawTotal !== 0 ? 100 / rawTotal : 1;

    // Show scaled row when outside 99.5% - 100.5%
    const needsScaling = rawTotal < 99.5 || rawTotal > 100.5;

    return { rawTotal, scaleFactor, needsScaling };
  }, [data?.totals, data?.mode]);

  // Group rows by primary classification when groupBy is set
  const { groupedData, groupTotals } = useMemo(() => {
    if (!groupBy || !sortedRows.length) {
      return { groupedData: null, groupTotals: null };
    }

    const primaryKey = data?.mode === "exposure" ? "primary" : `primary_${groupBy}`;
    const groups: Record<string, any[]> = {};

    for (const row of sortedRows) {
      const groupName = row[primaryKey] || "Unknown";
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(row);
    }

    // Calculate aggregated values for each group
    const groupRows: any[] = [];
    const totals: Record<string, any> = {};

    for (const [groupName, holdings] of Object.entries(groups)) {
      const totalWeight = holdings.reduce((sum, h) => sum + (h.weight || 0), 0);

      const groupRow: any = {
        name: groupName,
        isGroup: true,
        weight: totalWeight,
        holdings: holdings,
        holdingCount: holdings.length,
      };

      if (data?.mode === "field") {
        // For fundamental fields: weighted average value, sum contributions
        let weightedValueSum = 0;
        let contributionSum = 0;
        let validWeightSum = 0;

        for (const h of holdings) {
          if (h.value !== null && h.value !== undefined) {
            weightedValueSum += (h.weight || 0) * h.value;
            validWeightSum += h.weight || 0;
          }
          contributionSum += h.contribution || 0;
        }

        groupRow.value = validWeightSum > 0 ? weightedValueSum / validWeightSum : null;
        groupRow.contribution = contributionSum;
        groupRow.pct_of_total = data.total ? (contributionSum / data.total) * 100 : 0;
      } else {
        // For exposures: sum contributions for each category
        for (const cat of displayCategories) {
          if (cat === OTHER_BUCKET) {
            const avgValue = holdings.reduce((sum, h) => sum + getOtherValue(h, "value"), 0) / holdings.length;
            const sumContrib = holdings.reduce((sum, h) => sum + getOtherValue(h, "contrib"), 0);
            // Calculate % of total for Other: sum of contributions / sum of totals for other categories
            const otherTotalSum = otherCategories.reduce((sum, c) => sum + (data?.totals?.[c] || 0), 0);
            const pctOfTotal = otherTotalSum > 0 ? (sumContrib / otherTotalSum) * 100 : 0;
            groupRow[`value_${cat}`] = avgValue;
            groupRow[`contrib_${cat}`] = sumContrib;
            groupRow[`pct_${cat}`] = pctOfTotal;
          } else {
            const avgValue = holdings.reduce((sum, h) => sum + (h[`value_${cat}`] || 0), 0) / holdings.length;
            const sumContrib = holdings.reduce((sum, h) => sum + (h[`contrib_${cat}`] || 0), 0);
            const catTotal = data?.totals?.[cat] || 0;
            const pctOfTotal = catTotal > 0 ? (sumContrib / catTotal) * 100 : 0;
            groupRow[`value_${cat}`] = avgValue;
            groupRow[`contrib_${cat}`] = sumContrib;
            groupRow[`pct_${cat}`] = pctOfTotal;
          }
        }
      }

      groupRows.push(groupRow);
      totals[groupName] = groupRow;
    }

    // Sort groups by weight descending
    groupRows.sort((a, b) => (b.weight || 0) - (a.weight || 0));

    return { groupedData: groupRows, groupTotals: totals };
  }, [sortedRows, groupBy, data?.mode, data?.total, displayCategories, getOtherValue]);

  // Numeric banding: group holdings by value ranges when in field mode with banding enabled
  const bandedData = useMemo(() => {
    // Only apply when in field mode with banding enabled and no exposure grouping
    if (!bandingEnabled || data?.mode !== "field" || groupBy || !sortedRows.length) {
      return null;
    }

    // Helper to determine which band a value falls into
    const getBandForValue = (value: number | null | undefined): string => {
      if (value === null || value === undefined) return "No Data";

      // Calculate which band index this value falls into
      const bandIndex = Math.floor((value - bandStart) / bandSize);
      const bandLower = bandStart + (bandIndex * bandSize);
      const bandUpper = bandLower + bandSize;

      // Format the band label, e.g., "1 (0.5-1.5)" or just "(0.5-1.5)"
      // For scores like Bullseye, the midpoint is a nice label
      const midpoint = bandLower + (bandSize / 2);

      // If midpoint is a nice round number, use it as label
      if (Number.isInteger(midpoint) || (midpoint * 10) % 1 === 0) {
        return `${midpoint} (${bandLower.toFixed(1)}-${bandUpper.toFixed(1)})`;
      }
      return `${bandLower.toFixed(1)}-${bandUpper.toFixed(1)}`;
    };

    // Group holdings into bands
    const bands: Record<string, any[]> = {};

    for (const row of sortedRows) {
      const bandName = getBandForValue(row.value);
      if (!bands[bandName]) {
        bands[bandName] = [];
      }
      bands[bandName].push(row);
    }

    // Calculate aggregated values for each band
    const bandRows: any[] = [];

    for (const [bandName, holdings] of Object.entries(bands)) {
      const totalWeight = holdings.reduce((sum, h) => sum + (h.weight || 0), 0);

      // For field mode: weighted average value, sum contributions
      let weightedValueSum = 0;
      let contributionSum = 0;
      let validWeightSum = 0;

      for (const h of holdings) {
        if (h.value !== null && h.value !== undefined) {
          weightedValueSum += (h.weight || 0) * h.value;
          validWeightSum += h.weight || 0;
        }
        contributionSum += h.contribution || 0;
      }

      const bandRow: any = {
        name: bandName,
        isGroup: true,
        isBand: true,
        weight: totalWeight,
        value: validWeightSum > 0 ? weightedValueSum / validWeightSum : null,
        contribution: contributionSum,
        pct_of_total: data?.total ? (contributionSum / data.total) * 100 : 0,
        holdings: holdings,
        holdingCount: holdings.length,
        // For sorting: extract lower bound of band
        _sortValue: bandName === "No Data" ? -Infinity : parseFloat(bandName.split("(")[1]?.split("-")[0] || bandName.split("-")[0]) || 0,
      };

      bandRows.push(bandRow);
    }

    // Sort bands by their numeric value (ascending)
    bandRows.sort((a, b) => a._sortValue - b._sortValue);

    return bandRows;
  }, [bandingEnabled, data?.mode, data?.total, groupBy, sortedRows, bandSize, bandStart]);

  // Toggle group expansion
  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  // Handle row click for navigation
  const handleRowClick = (row: any) => {
    const resourceId = row.resource_id;
    if (!resourceId) return;

    const isPortfolio = row.resource_type === "portfolio";
    const name = row.name || (isPortfolio ? 'Portfolio' : 'Product');

    // If in OS mode, open in a new window
    if (osContext) {
      if (isPortfolio) {
        osContext.openWindow({
          appId: 'portfolio',
          title: name,
          params: { id: resourceId },
        });
      } else {
        osContext.openWindow({
          appId: 'product',
          title: name,
          params: { id: resourceId },
        });
      }
    } else {
      // Classic layout - use React Router navigation
      if (isPortfolio) {
        navigate(`/portfolio/${resourceId}`);
      } else {
        navigate(`/product/${resourceId}`);
      }
    }
  };

  // Get the selected field's metadata
  const selectedFieldInfo = useMemo(() => {
    if (!options?.fundamental_fields) return null;
    return options.fundamental_fields.find((f) => f.id === selectedField);
  }, [options?.fundamental_fields, selectedField]);

  // Get the corresponding screener column for this field (for formatting info)
  const screenerColumnForField = useMemo(() => {
    if (!selectedField || screenerColumns.length === 0) return null;
    // Try to find by matching id directly
    const byId = screenerColumns.find((c) => c.id === selectedField);
    if (byId) return byId;
    // Try to find by matching field_path
    if (selectedFieldInfo?.field_path) {
      const byPath = screenerColumns.find((c) => c.field_path === selectedFieldInfo.field_path);
      if (byPath) return byPath;
    }
    return null;
  }, [selectedField, selectedFieldInfo?.field_path, screenerColumns]);

  // Check if the field's values should be displayed as percentages
  const isFieldPercentage = useMemo(() => {
    // First check screener column (most reliable)
    if (screenerColumnForField) {
      if (screenerColumnForField.formatting?.type === "percentage") return true;
      if (screenerColumnForField.data_type === "percentage") return true;
    }
    // Fallback to contribution field info
    if (!selectedFieldInfo) return false;
    const formattingType = selectedFieldInfo.formatting?.type?.toLowerCase() || "";
    if (formattingType === "percentage") return true;
    const dataType = selectedFieldInfo.data_type?.toLowerCase() || "";
    return dataType.includes("percent") || dataType.includes("pct");
  }, [screenerColumnForField, selectedFieldInfo]);

  // Get the unit label for the field (for column headers)
  const fieldUnitLabel = useMemo(() => {
    // First check screener column (most reliable)
    if (screenerColumnForField) {
      const formatting = screenerColumnForField.formatting;
      if (formatting?.type === "percentage") return "%";
      if (formatting?.type === "currency") {
        const currency = formatting.options?.currency || "GBP";
        return currency === "GBP" ? "£" : currency;
      }
      // Also check data_type
      if (screenerColumnForField.data_type === "percentage") return "%";
      if (screenerColumnForField.data_type === "currency") return "£";
    }
    // Fallback to contribution field info
    if (!selectedFieldInfo) return "";
    const formattingType = selectedFieldInfo.formatting?.type?.toLowerCase() || "";
    if (formattingType === "percentage") return "%";
    if (formattingType === "currency") {
      const currency = selectedFieldInfo.formatting?.options?.currency || "GBP";
      return currency === "GBP" ? "£" : currency;
    }
    const dataType = selectedFieldInfo.data_type?.toLowerCase() || "";
    if (dataType.includes("percent") || dataType.includes("pct")) return "%";
    return "";
  }, [screenerColumnForField, selectedFieldInfo]);

  // Calculate data coverage for field mode
  const dataCoverage = useMemo(() => {
    if (!data?.rows || data.mode !== "field") return null;
    const total = data.rows.length;
    const withData = data.rows.filter((row: any) => row.value !== null && row.value !== undefined).length;
    return { withData, total };
  }, [data?.rows, data?.mode]);

  // Format value - weight column is always percentage, other values depend on field type
  const formatValue = (
    value: any,
    type: "weight" | "value" | "contribution" | "pct_of_total" | "exposure"
  ): string => {
    if (value === null || value === undefined) {
      return "—";
    }
    if (typeof value === "number") {
      // Weight and pct_of_total are always percentages (already in % form from backend)
      if (type === "weight" || type === "pct_of_total") {
        return `${value.toFixed(2)}%`;
      }
      // Exposure contributions are always percentages
      if (type === "exposure") {
        return `${value.toFixed(2)}%`;
      }
      // Value and contribution depend on field data type
      if (type === "value" || type === "contribution") {
        if (isFieldPercentage) {
          // Value is stored as decimal (0.05), convert to percentage (5.00%)
          return `${(value * 100).toFixed(2)}%`;
        }
        return value.toFixed(2);
      }
      return value.toFixed(2);
    }
    return String(value);
  };

  // Format the portfolio total
  const formatTotal = (total: number | undefined | null): string => {
    if (total === null || total === undefined) return "—";
    if (isFieldPercentage) {
      return `${(total * 100).toFixed(2)}%`;
    }
    return total.toFixed(2);
  };

  // Get aggregation method badge color
  const getAggregationBadgeColor = (method: string) => {
    switch (method) {
      case "weighted_average":
        return "blue";
      case "weighted_sum":
        return "green";
      default:
        return "zinc";
    }
  };

  // Loading spinner component - Meridian style
  const Spinner = ({ size = "md" }: { size?: "sm" | "md" | "lg" }) => {
    const sizeClasses = { sm: "h-4 w-4", md: "h-6 w-6", lg: "h-8 w-8" };
    return (
      <svg className={clsx("animate-spin", sizeClasses[size])} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    );
  };

  // Loading state - Meridian style
  if (optionsLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
          Loading contribution options...
        </span>
      </div>
    );
  }

  // Error state - Meridian style
  if (optionsError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-2xl text-red-500 dark:text-red-400"
          />
        </div>
        <p className="text-slate-600 dark:text-slate-400 text-center max-w-md">
          {optionsError}
        </p>
      </div>
    );
  }

  // Check if we have any options available
  const hasFields = options?.fundamental_fields && options.fundamental_fields.length > 0;
  const hasExposures = options?.exposure_groups && options.exposure_groups.length > 0;

  // Empty state - Meridian style
  if (!hasFields && !hasExposures) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center text-slate-400">
          <FontAwesomeIcon icon={faInfoCircle} className="text-2xl" />
        </div>
        <p className="text-slate-500 dark:text-slate-400 text-center">
          No contribution fields or exposures available for this portfolio.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Selector Row - Meridian card style */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 shadow-sm border border-slate-100 dark:border-slate-700">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Analysis Type - Pill select */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Analyse
            </label>
            <div className="relative">
              <select
                value={analysisType}
                onChange={(e) => setAnalysisType(e.target.value as AnalysisType)}
                className={clsx(
                  "appearance-none bg-slate-100 dark:bg-slate-700 border-none",
                  "text-sm font-medium text-slate-700 dark:text-slate-200",
                  "rounded-full py-2 pl-4 pr-8",
                  "focus:ring-2 focus:ring-indigo-500 focus:outline-none",
                  "cursor-pointer transition-colors hover:bg-slate-200 dark:hover:bg-slate-600"
                )}
              >
                {hasFields && <option value="field">Fundamental Field</option>}
                {hasExposures && <option value="exposure">Exposure Group</option>}
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-slate-500">
                <FontAwesomeIcon icon={faChevronDown} className="text-[10px]" />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-8 w-px bg-slate-200 dark:bg-slate-600 hidden sm:block" />

          {/* Field/Exposure Selector */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              {analysisType === "field" ? "Field" : "Exposure"}
            </label>
            {analysisType === "field" ? (
              <SearchableSelect<XRayContributionField>
                items={options?.fundamental_fields || []}
                value={selectedField}
                onChange={setSelectedField}
                getId={(f) => f.id}
                getLabel={(f) => f.name}
                placeholder="Search fields..."
              />
            ) : (
              <SearchableSelect<XRayExposureGroup>
                items={options?.exposure_groups || []}
                value={selectedExposure}
                onChange={setSelectedExposure}
                getId={(g) => g.id}
                getLabel={(g) => g.name}
                placeholder="Search exposures..."
              />
            )}
          </div>

          {/* Aggregation Method Badge (for field mode) */}
          {data?.mode === "field" && data.aggregation_method && (
            <Badge color={getAggregationBadgeColor(data.aggregation_method)} className="px-3 py-1">
              {data.aggregation_method.replace("_", " ")}
            </Badge>
          )}

          {/* Divider */}
          <div className="h-8 w-px bg-slate-200 dark:bg-slate-600 hidden sm:block" />

          {/* Holdings Source Selector */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Holdings
            </label>
            <div className="relative">
              <select
                value={holdingsSource}
                onChange={(e) => setHoldingsSource(e.target.value as HoldingsSource)}
                className={clsx(
                  "appearance-none bg-slate-100 dark:bg-slate-700 border-none",
                  "text-sm font-medium text-slate-700 dark:text-slate-200",
                  "rounded-full py-2 pl-4 pr-8",
                  "focus:ring-2 focus:ring-indigo-500 focus:outline-none",
                  "cursor-pointer transition-colors hover:bg-slate-200 dark:hover:bg-slate-600"
                )}
              >
                <option value="latest">Latest</option>
                <option value="target">Target / Manual</option>
                <option value="draft">Draft</option>
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-slate-500">
                <FontAwesomeIcon icon={faChevronDown} className="text-[10px]" />
              </div>
            </div>
            {/* Show holdings source from response */}
            {data?.holdings_source && (
              <Badge
                color={
                  data.holdings_source === "Draft" ? "yellow" :
                    data.holdings_source === "Target" ? "green" :
                      "zinc"
                }
                className="text-[10px] px-2 py-0.5"
              >
                {data.holdings_source}
              </Badge>
            )}
          </div>

          {/* Divider */}
          {hasExposures && <div className="h-8 w-px bg-slate-200 dark:bg-slate-600 hidden sm:block" />}

          {/* Group By dropdown */}
          {hasExposures && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Group by
              </label>
              <div className="relative">
                <select
                  value={groupBy}
                  onChange={(e) => {
                    setGroupBy(e.target.value);
                    setExpandedGroups(new Set());
                  }}
                  className={clsx(
                    "appearance-none bg-slate-100 dark:bg-slate-700 border-none",
                    "text-sm font-medium text-slate-700 dark:text-slate-200",
                    "rounded-full py-2 pl-4 pr-8",
                    "focus:ring-2 focus:ring-indigo-500 focus:outline-none",
                    "cursor-pointer transition-colors hover:bg-slate-200 dark:hover:bg-slate-600"
                  )}
                >
                  <option value="">None</option>
                  {options?.exposure_groups.map((eg) => (
                    <option key={eg.id} value={eg.id}>
                      {eg.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-slate-500">
                  <FontAwesomeIcon icon={faChevronDown} className="text-[10px]" />
                </div>
              </div>
            </div>
          )}

          {/* Numeric Banding Controls - only show in field mode */}
          {analysisType === "field" && selectedField && !groupBy && (
            <>
              {/* Divider */}
              <div className="h-8 w-px bg-slate-200 dark:bg-slate-600 hidden sm:block" />

              <div className="flex items-center gap-3">
                {/* Banding toggle */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Band Values
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={bandingEnabled}
                    onClick={() => {
                      setBandingEnabled(!bandingEnabled);
                      setExpandedGroups(new Set());
                    }}
                    className={clsx(
                      "relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2",
                      bandingEnabled ? "bg-indigo-500" : "bg-slate-300 dark:bg-slate-600"
                    )}
                  >
                    <span
                      className={clsx(
                        "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                        bandingEnabled ? "translate-x-4" : "translate-x-0"
                      )}
                    />
                  </button>
                </label>

                {/* Band configuration - only show when enabled */}
                {bandingEnabled && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 dark:text-slate-400">Size:</span>
                    <input
                      type="number"
                      value={bandSize}
                      onChange={(e) => setBandSize(parseFloat(e.target.value) || 1)}
                      step="0.5"
                      min="0.1"
                      className={clsx(
                        "w-16 px-2 py-1 text-sm rounded-lg text-center",
                        "bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600",
                        "text-slate-700 dark:text-slate-200",
                        "focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                      )}
                    />
                    <span className="text-xs text-slate-500 dark:text-slate-400">Start:</span>
                    <input
                      type="number"
                      value={bandStart}
                      onChange={(e) => setBandStart(parseFloat(e.target.value) || 0)}
                      step="0.5"
                      className={clsx(
                        "w-16 px-2 py-1 text-sm rounded-lg text-center",
                        "bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600",
                        "text-slate-700 dark:text-slate-200",
                        "focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                      )}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Loading indicator */}
          {loading && (
            <div className="ml-auto">
              <Spinner size="sm" />
            </div>
          )}
        </div>
      </div>

      {/* Error State - Meridian style */}
      {error && (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-xl text-red-500 dark:text-red-400"
            />
          </div>
          <p className="text-slate-600 dark:text-slate-400 text-center text-sm">
            {error}
          </p>
        </div>
      )}

      {/* Initial Loading State - Meridian style */}
      {loading && !data && (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center">
            <Spinner />
          </div>
          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
            Loading contribution data...
          </span>
        </div>
      )}

      {/* Field Contribution Table */}
      {!error && data?.mode === "field" && (
        <div className={clsx(
          "bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden transition-opacity duration-200",
          loading && "opacity-60"
        )}>
          {/* Summary Header - Meridian style */}
          <div className="px-5 py-5 border-b border-slate-100 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800">
            <div className="flex items-start justify-between flex-wrap gap-4">
              {/* Left: Field name and total */}
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
                  {selectedFieldInfo?.name || "Field Analysis"}
                </h3>
                <div className="flex items-baseline gap-3">
                  <span className="text-3xl font-bold text-indigo-600 dark:text-indigo-400 tabular-nums">
                    {formatTotal(data.total)}
                  </span>
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Portfolio Total
                  </span>
                </div>
              </div>
              {/* Right: Stats */}
              <div className="flex items-center gap-4 text-right">
                {dataCoverage && (
                  <div>
                    <div className="text-sm font-bold text-slate-900 dark:text-white">
                      {dataCoverage.withData} <span className="text-slate-400 font-normal">of</span> {dataCoverage.total}
                    </div>
                    <div className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Holdings with data
                    </div>
                  </div>
                )}
                <div className="h-8 w-px bg-slate-200 dark:bg-slate-600" />
                <div>
                  <div className="text-sm font-bold text-slate-900 dark:text-white">
                    {data.as_at}
                  </div>
                  <div className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    As at
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Methodology explainer - Contextual */}
          <div className="mx-5 mt-4 mb-3 p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-100 dark:border-slate-600/50 flex items-start gap-3">
            <FontAwesomeIcon icon={faInfoCircle} className="text-indigo-500 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              {data.aggregation_method === "weighted_sum" ? (
                <>
                  Showing how each holding contributes to the portfolio's total <strong className="text-slate-800 dark:text-slate-100">{selectedFieldInfo?.name?.toLowerCase()}</strong>.
                  Each contribution = holding weight × value. The portfolio total ({formatTotal(data.total)}) is the sum of all contributions.
                </>
              ) : (
                <>
                  Showing how each holding contributes to the portfolio's <strong className="text-slate-800 dark:text-slate-100">{selectedFieldInfo?.name?.toLowerCase()}</strong>.
                  Each contribution = holding weight × value. The portfolio total ({formatTotal(data.total)}) is the weighted average across all holdings.
                </>
              )}
            </p>
          </div>

          <div className="overflow-x-auto">

            <Table dense striped data-xray-table className="[&_th]:!bg-slate-50 [&_th]:dark:!bg-slate-700">
              <TableHead className="sticky top-0 z-10">
                <TableRow>
                  <TableHeader
                    className="text-left cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Holding</span>
                      {sortBy === "name" && (
                        <FontAwesomeIcon
                          icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                          className="h-2.5 w-2.5 text-indigo-500"
                        />
                      )}
                    </div>
                  </TableHeader>
                  <TableHeader
                    className="text-right cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                    onClick={() => handleSort("weight")}
                  >
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Weight</span>
                      {sortBy === "weight" && (
                        <FontAwesomeIcon
                          icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                          className="h-2.5 w-2.5 text-indigo-500"
                        />
                      )}
                    </div>
                  </TableHeader>
                  <TableHeader
                    className="text-right cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                    onClick={() => handleSort("value")}
                  >
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                        Value{fieldUnitLabel && ` (${fieldUnitLabel})`}
                      </span>
                      {sortBy === "value" && (
                        <FontAwesomeIcon
                          icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                          className="h-2.5 w-2.5 text-indigo-500"
                        />
                      )}
                    </div>
                  </TableHeader>
                  <TableHeader
                    className="text-right cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                    onClick={() => handleSort("contribution")}
                  >
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                        Contrib{fieldUnitLabel && ` (${fieldUnitLabel})`}
                      </span>
                      {sortBy === "contribution" && (
                        <FontAwesomeIcon
                          icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                          className="h-2.5 w-2.5 text-indigo-500"
                        />
                      )}
                    </div>
                  </TableHeader>
                  <TableHeader
                    className="text-right cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                    onClick={() => handleSort("pct_of_total")}
                  >
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">% Total</span>
                      {sortBy === "pct_of_total" && (
                        <FontAwesomeIcon
                          icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                          className="h-2.5 w-2.5 text-indigo-500"
                        />
                      )}
                    </div>
                  </TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {bandedData ? (
                  // Banded view - group by value ranges
                  bandedData.map((band) => (
                    <>
                      {/* Band header row - Meridian style with gradient */}
                      <TableRow
                        key={band.name}
                        className="cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors bg-gradient-to-r from-amber-50/50 to-slate-50/50 dark:from-amber-900/10 dark:to-slate-700/20"
                        onClick={() => toggleGroup(band.name)}
                      >
                        <TableCell className="font-semibold">
                          <div className="flex items-center gap-2">
                            <div className={clsx(
                              "w-5 h-5 rounded-md flex items-center justify-center transition-all duration-200",
                              expandedGroups.has(band.name)
                                ? "bg-amber-100 dark:bg-amber-900/30"
                                : "bg-slate-100 dark:bg-slate-600"
                            )}>
                              <FontAwesomeIcon
                                icon={expandedGroups.has(band.name) ? faChevronDown : faChevronRight}
                                className={clsx(
                                  "h-2.5 w-2.5 transition-colors",
                                  expandedGroups.has(band.name)
                                    ? "text-amber-600 dark:text-amber-400"
                                    : "text-slate-500 dark:text-slate-400"
                                )}
                              />
                            </div>
                            <span className="text-slate-900 dark:text-white font-mono">{band.name}</span>
                            <Badge color="amber" className="text-[10px] px-1.5 py-0.5">
                              {band.holdingCount}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-slate-900 dark:text-white">
                          {formatValue(band.weight, "weight")}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-slate-900 dark:text-white">
                          {formatValue(band.value, "value")}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-slate-900 dark:text-white">
                          {formatValue(band.contribution, "contribution")}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-slate-900 dark:text-white">
                          {formatValue(band.pct_of_total, "pct_of_total")}
                        </TableCell>
                      </TableRow>
                      {/* Expanded holdings in band */}
                      {expandedGroups.has(band.name) &&
                        band.holdings.map((row: any, idx: number) => (
                          <TableRow key={row.resource_id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                            <TableCell className="pl-10 text-slate-500 dark:text-slate-400">
                              <span
                                className="hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer transition-colors duration-150"
                                onClick={(e) => { e.stopPropagation(); handleRowClick(row); }}
                              >
                                {row.name}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                              {formatValue(row.weight, "weight")}
                            </TableCell>
                            <TableCell className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                              {formatValue(row.value, "value")}
                            </TableCell>
                            <TableCell className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                              {formatValue(row.contribution, "contribution")}
                            </TableCell>
                            <TableCell className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                              {formatValue(row.pct_of_total, "pct_of_total")}
                            </TableCell>
                          </TableRow>
                        ))}
                    </>
                  ))
                ) : groupBy && groupedData ? (
                  // Grouped view with expandable rows
                  groupedData.map((group) => (
                    <>
                      {/* Group header row - Meridian style */}
                      <TableRow
                        key={group.name}
                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors bg-slate-50/50 dark:bg-slate-700/20"
                        onClick={() => toggleGroup(group.name)}
                      >
                        <TableCell className="font-semibold">
                          <div className="flex items-center gap-2">
                            <div className={clsx(
                              "w-5 h-5 rounded-md flex items-center justify-center transition-all duration-200",
                              expandedGroups.has(group.name)
                                ? "bg-indigo-100 dark:bg-indigo-900/30"
                                : "bg-slate-100 dark:bg-slate-600"
                            )}>
                              <FontAwesomeIcon
                                icon={expandedGroups.has(group.name) ? faChevronDown : faChevronRight}
                                className={clsx(
                                  "h-2.5 w-2.5 transition-colors",
                                  expandedGroups.has(group.name)
                                    ? "text-indigo-600 dark:text-indigo-400"
                                    : "text-slate-500 dark:text-slate-400"
                                )}
                              />
                            </div>
                            <span className="text-slate-900 dark:text-white">{group.name}</span>
                            <Badge color="zinc" className="text-[10px] px-1.5 py-0.5">
                              {group.holdingCount}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-slate-900 dark:text-white">
                          {formatValue(group.weight, "weight")}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-slate-900 dark:text-white">
                          {formatValue(group.value, "value")}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-slate-900 dark:text-white">
                          {formatValue(group.contribution, "contribution")}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-slate-900 dark:text-white">
                          {formatValue(group.pct_of_total, "pct_of_total")}
                        </TableCell>
                      </TableRow>
                      {/* Expanded holdings */}
                      {expandedGroups.has(group.name) &&
                        group.holdings.map((row: any, idx: number) => (
                          <TableRow key={row.resource_id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                            <TableCell className="pl-10 text-slate-500 dark:text-slate-400">
                              <span
                                className="hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer transition-colors duration-150"
                                onClick={(e) => { e.stopPropagation(); handleRowClick(row); }}
                              >
                                {row.name}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                              {formatValue(row.weight, "weight")}
                            </TableCell>
                            <TableCell className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                              {formatValue(row.value, "value")}
                            </TableCell>
                            <TableCell className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                              {formatValue(row.contribution, "contribution")}
                            </TableCell>
                            <TableCell className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                              {formatValue(row.pct_of_total, "pct_of_total")}
                            </TableCell>
                          </TableRow>
                        ))}
                    </>
                  ))
                ) : (
                  // Flat view (no grouping)
                  sortedRows.map((row, idx) => {
                    const hasNoData = row.value === null || row.value === undefined;
                    return (
                      <TableRow
                        key={row.resource_id || idx}
                        className={clsx(
                          "hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors group",
                          hasNoData && "opacity-60"
                        )}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span
                              className="text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer transition-colors duration-150 font-medium"
                              onClick={() => handleRowClick(row)}
                            >
                              {row.name}
                            </span>
                            {hasNoData && (
                              <span className="text-[10px] text-slate-400 dark:text-slate-500 italic">no data</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-slate-600 dark:text-slate-300">
                          {formatValue(row.weight, "weight")}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-slate-600 dark:text-slate-300">
                          {formatValue(row.value, "value")}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-slate-600 dark:text-slate-300">
                          {formatValue(row.contribution, "contribution")}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-slate-600 dark:text-slate-300">
                          {formatValue(row.pct_of_total, "pct_of_total")}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
              {/* Totals row - Meridian prominent style */}
              <tfoot>
                <TableRow className="bg-gradient-to-r from-indigo-50 to-slate-50 dark:from-indigo-900/20 dark:to-slate-800 font-bold border-t-2 border-indigo-200 dark:border-indigo-800">
                  <TableCell className="text-slate-900 dark:text-white">
                    <span className="text-xs font-bold uppercase tracking-wider">Total</span>
                  </TableCell>
                  <TableCell className="text-right text-slate-900 dark:text-white tabular-nums">
                    {formatValue(
                      sortedRows.reduce((sum, row) => sum + (row.weight || 0), 0),
                      "weight"
                    )}
                  </TableCell>
                  <TableCell className="text-right text-slate-400">—</TableCell>
                  <TableCell className="text-right text-indigo-700 dark:text-indigo-300 tabular-nums">
                    {formatTotal(data.total)}
                  </TableCell>
                  <TableCell className="text-right text-slate-900 dark:text-white tabular-nums">
                    100.00%
                  </TableCell>
                </TableRow>
              </tfoot>
            </Table>
          </div>
        </div>
      )}

      {/* Exposure Contribution Table */}
      {!error && data?.mode === "exposure" && (
        <div className={clsx(
          "bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden transition-opacity duration-200",
          loading && "opacity-60"
        )}>
          {/* Table Header Bar */}
          <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm font-medium text-slate-900 dark:text-white">
                Exposure: <span className="text-indigo-600 dark:text-indigo-400 font-bold">{data.exposure?.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
              </span>
              <span className="text-xs text-slate-400 dark:text-slate-500">
                As at {data.as_at}
              </span>
              {otherCategories.length > 0 && (
                <Badge color="zinc" className="text-[10px]">
                  {typeof topN === "number" ? topN : "all"} of {data.categories?.length} categories
                </Badge>
              )}
            </div>
            {/* Top N selector - Meridian pill style */}
            {data.categories && data.categories.length > 5 && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Show</span>
                <div className="relative">
                  <select
                    value={topN}
                    onChange={(e) => setTopN(e.target.value === "all" ? "all" : Number(e.target.value) as TopNOption)}
                    className={clsx(
                      "appearance-none bg-slate-100 dark:bg-slate-700 border-none",
                      "text-sm font-medium text-slate-700 dark:text-slate-200",
                      "rounded-full py-1.5 pl-3 pr-7",
                      "focus:ring-2 focus:ring-indigo-500 focus:outline-none",
                      "cursor-pointer transition-colors hover:bg-slate-200 dark:hover:bg-slate-600"
                    )}
                  >
                    <option value={5}>Top 5</option>
                    <option value={10}>Top 10</option>
                    <option value={20}>Top 20</option>
                    <option value="all">All ({data.categories.length})</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none text-slate-500">
                    <FontAwesomeIcon icon={faChevronDown} className="text-[8px]" />
                  </div>
                </div>
              </div>
            )}
            {/* Raw total indicator - shows when not ~100% */}
            {exposureScaling.needsScaling && (
              <div className="flex items-center gap-2">
                <div className="h-4 w-px bg-slate-300 dark:bg-slate-600" />
                <Badge
                  color="yellow"
                  className="text-[10px] px-2 py-0.5"
                >
                  Raw total: {exposureScaling.rawTotal.toFixed(1)}%
                </Badge>
              </div>
            )}
          </div>

          {/* Methodology explainer for exposures - Meridian info card style */}
          <div className="mx-5 mt-4 mb-3 p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-100 dark:border-slate-600/50 flex items-start gap-3">
            <FontAwesomeIcon icon={faInfoCircle} className="text-indigo-500 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
              <strong className="text-slate-800 dark:text-slate-100">Exposure Analysis:</strong> Values show each holding's exposure %. Contributions = Weight × Value. % of Total shows each holding's contribution as a percentage of the portfolio's total exposure to that category.
              {exposureScaling.needsScaling && (
                <span className="ml-1">
                  <strong className="text-amber-600 dark:text-amber-400">Note:</strong> Raw totals sum to {exposureScaling.rawTotal.toFixed(1)}%. A scaled row is shown below.
                </span>
              )}
            </p>
          </div>

          <div className="overflow-auto pb-4" style={{ maxHeight: "calc(100vh - 350px)", minHeight: "400px" }}>

            <Table dense striped data-xray-table>
              <TableHead className="sticky top-0 z-20">
                {/* Group header row - Strong section colors */}
                <TableRow>
                  <TableHeader
                    rowSpan={2}
                    className="text-left align-bottom !bg-slate-100 dark:!bg-slate-700 sticky left-0 z-30 border-r-2 border-slate-300 dark:border-slate-600"
                  >
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">Holding</span>
                  </TableHeader>
                  <TableHeader
                    rowSpan={2}
                    className="text-right align-bottom !bg-slate-100 dark:!bg-slate-700 sticky left-[200px] z-30 border-r-2 border-slate-300 dark:border-slate-600"
                  >
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">Weight</span>
                  </TableHeader>
                  <TableHeader
                    colSpan={displayCategories.length + 1}
                    className="text-center !bg-blue-200 dark:!bg-blue-800 border-b-2 border-blue-400 dark:border-blue-600"
                  >
                    <span className="text-sm font-bold uppercase tracking-wider text-blue-800 dark:text-blue-100">Values</span>
                  </TableHeader>
                  <TableHeader
                    colSpan={displayCategories.length + 1}
                    className="text-center !bg-emerald-200 dark:!bg-emerald-800 border-b-2 border-emerald-400 dark:border-emerald-600 border-l-4 border-l-slate-300 dark:border-l-slate-600"
                  >
                    <span className="text-sm font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-100">Contributions</span>
                  </TableHeader>
                  <TableHeader
                    colSpan={displayCategories.length}
                    className="text-center !bg-violet-200 dark:!bg-violet-800 border-b-2 border-violet-400 dark:border-violet-600 border-l-4 border-l-slate-300 dark:border-l-slate-600"
                  >
                    <span className="text-sm font-bold uppercase tracking-wider text-violet-800 dark:text-violet-100">% of Total</span>
                  </TableHeader>
                </TableRow>
                {/* Category sub-header row */}
                <TableRow>
                  {/* Values columns */}
                  {displayCategories.map((cat, idx) => (
                    <TableHeader
                      key={`value_${cat}`}
                      className={clsx(
                        "text-right whitespace-nowrap !bg-blue-100 dark:!bg-blue-900 cursor-pointer hover:!bg-blue-200 dark:hover:!bg-blue-800 transition-colors",
                        idx === 0 && "border-l-0"
                      )}
                      onClick={() => handleSort(`value_${cat}`)}
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-blue-700 dark:text-blue-200">
                          {cat === OTHER_BUCKET ? `Other (${otherCategories.length})` : cat}
                        </span>
                        {sortBy === `value_${cat}` && (
                          <FontAwesomeIcon
                            icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                            className="h-2 w-2 text-blue-600"
                          />
                        )}
                      </div>
                    </TableHeader>
                  ))}
                  {/* Values Total column */}
                  <TableHeader
                    className="text-right whitespace-nowrap !bg-blue-300 dark:!bg-blue-700 cursor-pointer hover:!bg-blue-400 dark:hover:!bg-blue-600 transition-colors border-l-2 border-blue-400 dark:border-blue-500"
                    onClick={() => handleSort("value_total")}
                  >
                    <div className="flex items-center justify-end gap-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-blue-900 dark:text-blue-100">
                        Total
                      </span>
                      {sortBy === "value_total" && (
                        <FontAwesomeIcon
                          icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                          className="h-2 w-2 text-blue-800"
                        />
                      )}
                    </div>
                  </TableHeader>
                  {/* Contributions columns */}
                  {displayCategories.map((cat, idx) => (
                    <TableHeader
                      key={`contrib_${cat}`}
                      className={clsx(
                        "text-right whitespace-nowrap !bg-emerald-100 dark:!bg-emerald-900 cursor-pointer hover:!bg-emerald-200 dark:hover:!bg-emerald-800 transition-colors",
                        idx === 0 && "border-l-4 border-l-slate-300 dark:border-l-slate-600"
                      )}
                      onClick={() => handleSort(`contrib_${cat}`)}
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-200">
                          {cat === OTHER_BUCKET ? `Other (${otherCategories.length})` : cat}
                        </span>
                        {sortBy === `contrib_${cat}` && (
                          <FontAwesomeIcon
                            icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                            className="h-2 w-2 text-emerald-600"
                          />
                        )}
                      </div>
                    </TableHeader>
                  ))}
                  {/* Contributions Total column */}
                  <TableHeader
                    className="text-right whitespace-nowrap !bg-emerald-300 dark:!bg-emerald-700 cursor-pointer hover:!bg-emerald-400 dark:hover:!bg-emerald-600 transition-colors border-l-2 border-emerald-400 dark:border-emerald-500"
                    onClick={() => handleSort("contrib_total")}
                  >
                    <div className="flex items-center justify-end gap-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-900 dark:text-emerald-100">
                        Total
                      </span>
                      {sortBy === "contrib_total" && (
                        <FontAwesomeIcon
                          icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                          className="h-2 w-2 text-emerald-800"
                        />
                      )}
                    </div>
                  </TableHeader>
                  {/* % of Total columns */}
                  {displayCategories.map((cat, idx) => (
                    <TableHeader
                      key={`pct_${cat}`}
                      className={clsx(
                        "text-right whitespace-nowrap !bg-violet-100 dark:!bg-violet-900 cursor-pointer hover:!bg-violet-200 dark:hover:!bg-violet-800 transition-colors",
                        idx === 0 && "border-l-4 border-l-slate-300 dark:border-l-slate-600"
                      )}
                      onClick={() => handleSort(`pct_${cat}`)}
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-200">
                          {cat === OTHER_BUCKET ? `Other (${otherCategories.length})` : cat}
                        </span>
                        {sortBy === `pct_${cat}` && (
                          <FontAwesomeIcon
                            icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                            className="h-2 w-2 text-violet-600"
                          />
                        )}
                      </div>
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groupBy && groupedData ? (
                  // Grouped view with expandable rows - Meridian style
                  groupedData.map((group) => (
                    <>
                      {/* Group header row */}
                      <TableRow
                        key={group.name}
                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors bg-slate-50/50 dark:bg-slate-700/20"
                        onClick={() => toggleGroup(group.name)}
                      >
                        <TableCell className="font-semibold">
                          <div className="flex items-center gap-2">
                            <div className={clsx(
                              "w-5 h-5 rounded-md flex items-center justify-center transition-all duration-200",
                              expandedGroups.has(group.name)
                                ? "bg-indigo-100 dark:bg-indigo-900/30"
                                : "bg-slate-100 dark:bg-slate-600"
                            )}>
                              <FontAwesomeIcon
                                icon={expandedGroups.has(group.name) ? faChevronDown : faChevronRight}
                                className={clsx(
                                  "h-2.5 w-2.5 transition-colors",
                                  expandedGroups.has(group.name)
                                    ? "text-indigo-600 dark:text-indigo-400"
                                    : "text-slate-500 dark:text-slate-400"
                                )}
                              />
                            </div>
                            <span className="text-slate-900 dark:text-white">{group.name}</span>
                            <Badge color="zinc" className="text-[10px] px-1.5 py-0.5">
                              {group.holdingCount}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-slate-900 dark:text-white tabular-nums">
                          {formatValue(group.weight, "weight")}
                        </TableCell>
                        {/* Values */}
                        {displayCategories.map((cat) => (
                          <TableCell key={`value_${cat}`} className="text-right font-semibold text-blue-700 dark:text-blue-300 tabular-nums">
                            {formatValue(group[`value_${cat}`], "exposure")}
                          </TableCell>
                        ))}
                        {/* Values Total */}
                        <TableCell className="text-right font-semibold text-blue-800 dark:text-blue-200 tabular-nums bg-blue-100/50 dark:bg-blue-800/30 border-l-2 border-blue-300 dark:border-blue-600">
                          {formatValue(
                            group.holdings.reduce((sum: number, h: any) => sum + getRowValueTotal(h), 0) / group.holdings.length,
                            "exposure"
                          )}
                        </TableCell>
                        {/* Contributions */}
                        {displayCategories.map((cat) => (
                          <TableCell key={`contrib_${cat}`} className="text-right font-semibold text-emerald-700 dark:text-emerald-300 tabular-nums">
                            {formatValue(group[`contrib_${cat}`], "exposure")}
                          </TableCell>
                        ))}
                        {/* Contributions Total */}
                        <TableCell className="text-right font-semibold text-emerald-800 dark:text-emerald-200 tabular-nums bg-emerald-100/50 dark:bg-emerald-800/30 border-l-2 border-emerald-300 dark:border-emerald-600">
                          {formatValue(
                            group.holdings.reduce((sum: number, h: any) => sum + getRowContribTotal(h), 0),
                            "exposure"
                          )}
                        </TableCell>
                        {/* % of Total */}
                        {displayCategories.map((cat) => (
                          <TableCell key={`pct_${cat}`} className="text-right font-semibold text-violet-700 dark:text-violet-300 tabular-nums">
                            {formatValue(group[`pct_${cat}`], "exposure")}
                          </TableCell>
                        ))}
                      </TableRow>
                      {/* Expanded holdings */}
                      {expandedGroups.has(group.name) &&
                        group.holdings.map((row: any, idx: number) => (
                          <TableRow key={row.resource_id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                            <TableCell className="pl-10 text-slate-500 dark:text-slate-400">
                              <span
                                className="hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer transition-colors duration-150"
                                onClick={(e) => { e.stopPropagation(); handleRowClick(row); }}
                              >
                                {row.name}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                              {formatValue(row.weight, "weight")}
                            </TableCell>
                            {displayCategories.map((cat) => (
                              <TableCell key={`value_${cat}`} className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                                {formatValue(
                                  cat === OTHER_BUCKET ? getOtherValue(row, "value") : row[`value_${cat}`],
                                  "exposure"
                                )}
                              </TableCell>
                            ))}
                            {/* Values Total */}
                            <TableCell className={clsx(
                              "text-right tabular-nums font-medium border-l-2 border-blue-200 dark:border-blue-700",
                              getRowValueTotal(row) < 99.5
                                ? "text-amber-500 dark:text-amber-400"
                                : "text-blue-600 dark:text-blue-300"
                            )}>
                              {formatValue(getRowValueTotal(row), "exposure")}
                            </TableCell>
                            {displayCategories.map((cat) => (
                              <TableCell key={`contrib_${cat}`} className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                                {formatValue(
                                  cat === OTHER_BUCKET ? getOtherValue(row, "contrib") : row[`contrib_${cat}`],
                                  "exposure"
                                )}
                              </TableCell>
                            ))}
                            {/* Contributions Total */}
                            <TableCell className="text-right tabular-nums font-medium text-emerald-600 dark:text-emerald-300 border-l-2 border-emerald-200 dark:border-emerald-700">
                              {formatValue(getRowContribTotal(row), "exposure")}
                            </TableCell>
                            {displayCategories.map((cat) => (
                              <TableCell key={`pct_${cat}`} className="text-right text-slate-500 dark:text-slate-400 tabular-nums">
                                {formatValue(
                                  cat === OTHER_BUCKET ? getOtherValue(row, "pct") : row[`pct_${cat}`],
                                  "exposure"
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                    </>
                  ))
                ) : (
                  // Flat view (no grouping) - Meridian style
                  sortedRows.map((row, idx) => (
                    <TableRow key={row.resource_id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors group">
                      <TableCell className="sticky left-0 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/30 border-r-2 border-slate-200 dark:border-slate-600">
                        <span
                          className="text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer transition-colors duration-150 font-medium"
                          onClick={() => handleRowClick(row)}
                        >
                          {row.name}
                        </span>
                      </TableCell>
                      <TableCell className="sticky left-[200px] z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/30 text-right tabular-nums text-slate-600 dark:text-slate-300 border-r-2 border-slate-200 dark:border-slate-600">
                        {formatValue(row.weight, "weight")}
                      </TableCell>
                      {/* Values - blue tinted */}
                      {displayCategories.map((cat, catIdx) => (
                        <TableCell key={`value_${cat}`} className="text-right tabular-nums text-blue-700 dark:text-blue-300 bg-blue-50/30 dark:bg-blue-900/10">
                          {formatValue(
                            cat === OTHER_BUCKET ? getOtherValue(row, "value") : row[`value_${cat}`],
                            "exposure"
                          )}
                        </TableCell>
                      ))}
                      {/* Values Total - highlighted */}
                      <TableCell className={clsx(
                        "text-right tabular-nums font-semibold border-l-2 border-blue-300 dark:border-blue-600",
                        getRowValueTotal(row) < 99.5
                          ? "text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/20"
                          : "text-blue-800 dark:text-blue-200 bg-blue-100/50 dark:bg-blue-800/30"
                      )}>
                        {formatValue(getRowValueTotal(row), "exposure")}
                      </TableCell>
                      {/* Contributions - green tinted */}
                      {displayCategories.map((cat, catIdx) => (
                        <TableCell
                          key={`contrib_${cat}`}
                          className={clsx(
                            "text-right tabular-nums text-emerald-700 dark:text-emerald-300 bg-emerald-50/30 dark:bg-emerald-900/10",
                            catIdx === 0 && "border-l-4 border-l-slate-200 dark:border-l-slate-600"
                          )}
                        >
                          {formatValue(
                            cat === OTHER_BUCKET ? getOtherValue(row, "contrib") : row[`contrib_${cat}`],
                            "exposure"
                          )}
                        </TableCell>
                      ))}
                      {/* Contributions Total - highlighted */}
                      <TableCell className="text-right tabular-nums font-semibold text-emerald-800 dark:text-emerald-200 bg-emerald-100/50 dark:bg-emerald-800/30 border-l-2 border-emerald-300 dark:border-emerald-600">
                        {formatValue(getRowContribTotal(row), "exposure")}
                      </TableCell>
                      {/* % of Total - purple tinted */}
                      {displayCategories.map((cat, catIdx) => (
                        <TableCell
                          key={`pct_${cat}`}
                          className={clsx(
                            "text-right tabular-nums text-violet-700 dark:text-violet-300 bg-violet-50/30 dark:bg-violet-900/10",
                            catIdx === 0 && "border-l-4 border-l-slate-200 dark:border-l-slate-600"
                          )}
                        >
                          {formatValue(
                            cat === OTHER_BUCKET ? getOtherValue(row, "pct") : row[`pct_${cat}`],
                            "exposure"
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
              {/* Totals row - Strong styling, sticky at bottom */}
              <tfoot>
                <TableRow className="sticky bottom-0 z-20 bg-slate-200 dark:bg-slate-700 font-bold border-t-2 border-slate-400 dark:border-slate-500">
                  <TableCell className="sticky left-0 z-30 bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white border-r-2 border-slate-300 dark:border-slate-600">
                    <span className="text-xs font-bold uppercase tracking-wider">Total</span>
                  </TableCell>
                  <TableCell className="sticky left-[200px] z-30 bg-slate-200 dark:bg-slate-700 text-right text-slate-900 dark:text-white tabular-nums border-r-2 border-slate-300 dark:border-slate-600">
                    {formatValue(
                      sortedRows.reduce((sum, row) => sum + (row.weight || 0), 0),
                      "weight"
                    )}
                  </TableCell>
                  {/* Values totals - show same as contributions (portfolio total exposure per category) */}
                  {displayCategories.map((cat) => {
                    let total = 0;
                    if (cat === OTHER_BUCKET) {
                      for (const c of otherCategories) {
                        total += data.totals?.[c] || 0;
                      }
                    } else {
                      total = data.totals?.[cat] || 0;
                    }
                    return (
                      <TableCell key={`value_${cat}`} className="text-right text-blue-800 dark:text-blue-200 tabular-nums bg-blue-100 dark:bg-blue-900">
                        {formatValue(total, "exposure")}
                      </TableCell>
                    );
                  })}
                  {/* Values Total column */}
                  <TableCell className="text-right text-blue-900 dark:text-blue-100 tabular-nums font-bold bg-blue-200 dark:bg-blue-800 border-l-2 border-blue-300 dark:border-blue-600">
                    {formatValue(
                      Object.values(data.totals || {}).reduce((sum: number, val: any) => sum + (val || 0), 0),
                      "exposure"
                    )}
                  </TableCell>
                  {/* Contributions totals */}
                  {displayCategories.map((cat, catIdx) => {
                    let total = 0;
                    if (cat === OTHER_BUCKET) {
                      for (const c of otherCategories) {
                        total += data.totals?.[c] || 0;
                      }
                    } else {
                      total = data.totals?.[cat] || 0;
                    }
                    return (
                      <TableCell
                        key={`contrib_${cat}`}
                        className={clsx(
                          "text-right text-emerald-800 dark:text-emerald-200 tabular-nums bg-emerald-100 dark:bg-emerald-900",
                          catIdx === 0 && "border-l-4 border-l-slate-300 dark:border-l-slate-500"
                        )}
                      >
                        {formatValue(total, "exposure")}
                      </TableCell>
                    );
                  })}
                  {/* Contributions Total column */}
                  <TableCell className="text-right text-emerald-900 dark:text-emerald-100 tabular-nums font-bold bg-emerald-200 dark:bg-emerald-700 border-l-2 border-emerald-300 dark:border-emerald-500">
                    {formatValue(
                      Object.values(data.totals || {}).reduce((sum: number, val: any) => sum + (val || 0), 0),
                      "exposure"
                    )}
                  </TableCell>
                  {/* % of Total totals - always 100% */}
                  {displayCategories.map((cat, catIdx) => (
                    <TableCell
                      key={`pct_${cat}`}
                      className={clsx(
                        "text-right text-violet-800 dark:text-violet-200 tabular-nums bg-violet-100 dark:bg-violet-900",
                        catIdx === 0 && "border-l-4 border-l-slate-300 dark:border-l-slate-500"
                      )}
                    >
                      100.00%
                    </TableCell>
                  ))}
                </TableRow>
                {/* Scaled to 100% row - only shown when raw total is outside 99.5-100.5% */}
                {exposureScaling.needsScaling && (
                  <TableRow className="bg-indigo-100 dark:bg-indigo-900/40 font-medium border-t border-indigo-200 dark:border-indigo-700">
                    <TableCell className="sticky left-0 z-30 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200 border-r-2 border-indigo-200 dark:border-indigo-700">
                      <span className="text-[10px] font-bold uppercase tracking-wider">Scaled to 100%</span>
                    </TableCell>
                    <TableCell className="sticky left-[200px] z-30 bg-indigo-100 dark:bg-indigo-900/40 text-right text-indigo-600 dark:text-indigo-300 tabular-nums border-r-2 border-indigo-200 dark:border-indigo-700">
                      100.00%
                    </TableCell>
                    {/* Scaled Values */}
                    {displayCategories.map((cat) => {
                      let total = 0;
                      if (cat === OTHER_BUCKET) {
                        for (const c of otherCategories) {
                          total += data.totals?.[c] || 0;
                        }
                      } else {
                        total = data.totals?.[cat] || 0;
                      }
                      return (
                        <TableCell key={`scaled_value_${cat}`} className="text-right text-indigo-700 dark:text-indigo-300 tabular-nums bg-indigo-50 dark:bg-indigo-900/20">
                          {formatValue(total * exposureScaling.scaleFactor, "exposure")}
                        </TableCell>
                      );
                    })}
                    {/* Scaled Values Total */}
                    <TableCell className="text-right text-indigo-800 dark:text-indigo-200 tabular-nums font-bold bg-indigo-100 dark:bg-indigo-800/40 border-l-2 border-indigo-200 dark:border-indigo-600">
                      100.00%
                    </TableCell>
                    {/* Scaled Contributions */}
                    {displayCategories.map((cat, catIdx) => {
                      let total = 0;
                      if (cat === OTHER_BUCKET) {
                        for (const c of otherCategories) {
                          total += data.totals?.[c] || 0;
                        }
                      } else {
                        total = data.totals?.[cat] || 0;
                      }
                      return (
                        <TableCell
                          key={`scaled_contrib_${cat}`}
                          className={clsx(
                            "text-right text-indigo-700 dark:text-indigo-300 tabular-nums bg-indigo-50 dark:bg-indigo-900/20",
                            catIdx === 0 && "border-l-4 border-l-indigo-200 dark:border-l-indigo-700"
                          )}
                        >
                          {formatValue(total * exposureScaling.scaleFactor, "exposure")}
                        </TableCell>
                      );
                    })}
                    {/* Scaled Contributions Total */}
                    <TableCell className="text-right text-indigo-800 dark:text-indigo-200 tabular-nums font-bold bg-indigo-100 dark:bg-indigo-800/40 border-l-2 border-indigo-200 dark:border-indigo-600">
                      100.00%
                    </TableCell>
                    {/* Scaled % of Total - same as raw */}
                    {displayCategories.map((cat, catIdx) => (
                      <TableCell
                        key={`scaled_pct_${cat}`}
                        className={clsx(
                          "text-right text-indigo-700 dark:text-indigo-300 tabular-nums bg-indigo-50 dark:bg-indigo-900/20",
                          catIdx === 0 && "border-l-4 border-l-indigo-200 dark:border-l-indigo-700"
                        )}
                      >
                        100.00%
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </tfoot>
            </Table>
          </div>
        </div>
      )}

      {/* Empty State - Meridian style */}
      {!loading && !error && (!data || data.rows.length === 0) && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center text-slate-400">
              <FontAwesomeIcon icon={faInfoCircle} className="text-2xl" />
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-center">
              No data available for the selected {analysisType}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default XRayContributions;
